/**
 * The "ownership and revival" block shared by BrandEditor's Create and Edit
 * modals: ownership type, and — for a revived brand — the year it was revived
 * and the earlier brand it revives.
 *
 * Like the location and commerce blocks, the form state is one object so both
 * modals hold, reset and save it the same way.
 *
 * `yearRevived` and `predecessorBrand` only mean something when the brand's
 * status is `revived`, so they are shown, and written, only then. That mirrors
 * `yearCeased`, which follows the ceased statuses.
 */
import { useId } from 'react';
import type { BrandStatus, WatchBrand } from '../../types';

export type OwnershipType = NonNullable<WatchBrand['ownershipType']>;

export const OWNERSHIP_TYPE_OPTIONS: { value: OwnershipType; label: string }[] = [
  { value: 'independent', label: 'Independent' },
  { value: 'family-owned', label: 'Family-owned' },
  { value: 'private-equity', label: 'Private equity' },
  { value: 'public', label: 'Public' },
  { value: 'subsidiary', label: 'Subsidiary' },
];

export const BRAND_STATUS_OPTIONS: { value: BrandStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'dormant', label: 'Dormant' },
  { value: 'defunct', label: 'Defunct' },
  { value: 'revived', label: 'Revived' },
  { value: 'absorbed', label: 'Absorbed' },
];

export function isOwnershipType(value: unknown): value is OwnershipType {
  return OWNERSHIP_TYPE_OPTIONS.some((option) => option.value === value);
}

export function isBrandStatus(value: unknown): value is BrandStatus {
  return BRAND_STATUS_OPTIONS.some((option) => option.value === value);
}

export interface BrandIdentityForm {
  /** '' = not set. */
  ownershipType: OwnershipType | '';
  /** Kept as typed, so a half-entered year is not lost; parsed on save. */
  yearRevived: string;
  predecessorBrand: string;
}

export const emptyBrandIdentityForm: BrandIdentityForm = {
  ownershipType: '',
  yearRevived: '',
  predecessorBrand: '',
};

export function brandIdentityFormFromBrand(brand: Partial<WatchBrand>): BrandIdentityForm {
  return {
    ownershipType: isOwnershipType(brand.ownershipType) ? brand.ownershipType : '',
    yearRevived: brand.yearRevived !== undefined ? String(brand.yearRevived) : '',
    predecessorBrand: brand.predecessorBrand ?? '',
  };
}

/** A 4-digit year, or undefined for anything else (blank, partial, text). */
export function parseYear(value: string): number | undefined {
  const trimmed = value.trim();
  if (!/^\d{4}$/.test(trimmed)) return undefined;
  return Number(trimmed);
}

/** The values the form describes for a brand with this status. */
function identityValues(form: BrandIdentityForm, status: BrandStatus): Partial<WatchBrand> {
  const values: Partial<WatchBrand> = {};
  if (form.ownershipType) values.ownershipType = form.ownershipType;
  if (status === 'revived') {
    const year = parseYear(form.yearRevived);
    if (year !== undefined) values.yearRevived = year;
    const predecessor = form.predecessorBrand.trim();
    if (predecessor) values.predecessorBrand = predecessor;
  }
  return values;
}

/** What a NEW brand writes: only what was stated. */
export function brandIdentityCreatePayload(
  form: BrandIdentityForm,
  status: BrandStatus,
): Partial<WatchBrand> {
  return identityValues(form, status);
}

export const IDENTITY_KEYS = ['ownershipType', 'yearRevived', 'predecessorBrand'] as const;

export interface BrandIdentityUpdate {
  payload: Partial<WatchBrand>;
  /**
   * Stored fields to DELETE: the form no longer describes them — cleared by
   * hand, or the status moved off `revived` — and leaving them out of the
   * update would keep the old value on the document.
   */
  clear: (typeof IDENTITY_KEYS)[number][];
}

/** What an EDIT writes, and which stored fields it removes. */
export function brandIdentityUpdatePayload(
  form: BrandIdentityForm,
  status: BrandStatus,
  existing: WatchBrand,
): BrandIdentityUpdate {
  const payload = identityValues(form, status);
  const clear = IDENTITY_KEYS.filter(
    (key) => payload[key] === undefined && existing[key] !== undefined,
  );
  return { payload, clear };
}

interface BrandIdentityFieldsProps {
  value: BrandIdentityForm;
  onChange: (next: BrandIdentityForm) => void;
  /** The brand's lifecycle status; decides whether the revival fields show. */
  status: BrandStatus;
  /** Render a Status select with the lifecycle fields. */
  onStatusChange?: (status: BrandStatus) => void;
  /**
   * Which group to render. The brand form puts ownership type under Ownership &
   * Relationships and status + revival under History & Status — one form
   * value, two places. Defaults to both.
   */
  part?: 'all' | 'ownership' | 'lifecycle';
  disabled?: boolean;
}

const inputClass =
  'w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500';

export function BrandIdentityFields({
  value,
  onChange,
  status,
  onStatusChange,
  part = 'all',
  disabled = false,
}: BrandIdentityFieldsProps) {
  const id = useId();
  const yearInvalid = value.yearRevived.trim() !== '' && parseYear(value.yearRevived) === undefined;
  const showOwnership = part !== 'lifecycle';
  const showLifecycle = part !== 'ownership';
  return (
    <div className='space-y-4'>
      {(showOwnership || (showLifecycle && onStatusChange)) && (
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
          {showLifecycle && onStatusChange && (
            <div>
              <label htmlFor={`${id}-status`} className='block text-sm font-medium text-gray-700 mb-1'>
                Status
              </label>
              <select
                id={`${id}-status`}
                value={status}
                disabled={disabled}
                onChange={(e) => onStatusChange(e.target.value as BrandStatus)}
                className={inputClass}
                title='Lifecycle marker — display only, still browsable in Explore'
              >
                {BRAND_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          {showOwnership && (
            <div>
              <label htmlFor={`${id}-ownership`} className='block text-sm font-medium text-gray-700 mb-1'>
                Ownership type
              </label>
              <select
                id={`${id}-ownership`}
                value={value.ownershipType}
                disabled={disabled}
                onChange={(e) =>
                  onChange({ ...value, ownershipType: e.target.value as OwnershipType | '' })
                }
                className={inputClass}
              >
                <option value=''>— Not set —</option>
                {OWNERSHIP_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {value.ownershipType === 'subsidiary' && (
                <p className='text-xs text-gray-500 mt-1'>
                  A subsidiary should have its Parent Organization set as well.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {showLifecycle && status === 'revived' && (
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
          <div>
            <label htmlFor={`${id}-yearRevived`} className='block text-sm font-medium text-gray-700 mb-1'>
              Year revived
            </label>
            <input
              type='text'
              inputMode='numeric'
              id={`${id}-yearRevived`}
              value={value.yearRevived}
              disabled={disabled}
              onChange={(e) => onChange({ ...value, yearRevived: e.target.value })}
              placeholder='e.g. 2016'
              className={inputClass}
            />
            {yearInvalid && (
              <p className='text-xs text-amber-700 mt-1'>
                Enter a 4-digit year. Anything else is not saved.
              </p>
            )}
          </div>
          <div>
            <label htmlFor={`${id}-predecessor`} className='block text-sm font-medium text-gray-700 mb-1'>
              Predecessor brand
            </label>
            <input
              type='text'
              id={`${id}-predecessor`}
              value={value.predecessorBrand}
              disabled={disabled}
              onChange={(e) => onChange({ ...value, predecessorBrand: e.target.value })}
              placeholder='The earlier brand this one revives'
              className={inputClass}
            />
            <p className='text-xs text-gray-500 mt-1'>
              Only for a revival of an earlier, distinct brand — not a rename.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
